import api from './api';

// Get all reports from backend
export const getReports = async (type = 'my') => {
    try {
        const response = await api.get('/expenses', { params: { type } });
        return response.data;
    } catch (error) {
        console.error('Error loading reports:', error);
        return []; // Fallback to empty
    }
};

// Save a report (Create or Update)
export const saveReport = async (report) => {
    try {
        if (report.id) {
            // Update
            const response = await api.put(`/expenses/${report.id}`, report);
            return response.data;
        } else {
            // Create
            const response = await api.post('/expenses', report);
            return response.data;
        }
    } catch (error) {
        console.error('Error saving report:', error);
        throw error;
    }
};

// Get a single report by ID
export const getReport = async (id) => {
    try {
        const response = await api.get(`/expenses/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching report:', error);
        return null;
    }
};

// Delete a report
export const deleteReport = async (id) => {
    try {
        await api.delete(`/expenses/${id}`);
        return true;
    } catch (error) {
        console.error('Error deleting report:', error);
        return false;
    }
};

// Items are added, changed and removed with one small request each. The report is never sent back
// whole: with receipts attached that grew with every item and made each save slower than the last.
export const addExpenseItems = async (reportId, items) => {
    const response = await api.post(`/expenses/${reportId}/items`, { items });
    return response.data;
};

export const addExpenseItem = (reportId, item) => addExpenseItems(reportId, [item]);

export const updateExpenseItem = async (reportId, itemId, item) => {
    const response = await api.put(`/expenses/${reportId}/items/${itemId}`, item);
    return response.data;
};

export const removeExpenseItem = async (reportId, itemId) => {
    const response = await api.delete(`/expenses/${reportId}/items/${itemId}`);
    return response.data;
};

// Where an item's receipt file is served (same origin, so the session cookie goes with it).
export const receiptHref = (reportId, itemId) => `${api.defaults.baseURL}/expenses/${reportId}/items/${itemId}/receipt`;

// Calculate total (Client-side helper, though backend does it too)
const _calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
};

// Submit report for approval
export const submitReport = async (reportId) => {
    try {
        const response = await api.post(`/expenses/${reportId}/submit`);
        return response.data;
    } catch (error) {
        console.error('Error submitting report:', error);
        throw error;
    }
};

// Approve report
export const approveReport = async (reportId, comments = '') => {
    try {
        const response = await api.post(`/expenses/${reportId}/approve`, { comments });
        return response.data;
    } catch (error) {
        console.error('Error approving report:', error);
        throw error;
    }
};

// Reject report
export const rejectReport = async (reportId, comments = '') => {
    try {
        const response = await api.post(`/expenses/${reportId}/reject`, { comments });
        return response.data;
    } catch (error) {
        console.error('Error rejecting report:', error);
        throw error;
    }
};

// Get audit trail for report
export const getAuditTrail = async (reportId) => {
    try {
        const response = await api.get(`/expenses/${reportId}/audit`);
        return response.data;
    } catch (error) {
        console.error('Error fetching audit trail:', error);
        return [];
    }
};

// ── Background import of receipts (the server reads them; see ReceiptImportDialog) ─────────────

export const createImportBatch = async (reportId) => (await api.post(`/expenses/${reportId}/imports`, {})).data;

export const uploadImportFiles = async (reportId, batchId, files) => {
    const form = new FormData();
    files.forEach(file => form.append('files', file, file.name));
    const response = await api.post(`/expenses/${reportId}/imports/${batchId}/files`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 });
    return response.data;
};

export const startImport = async (reportId, batchId) => (await api.post(`/expenses/${reportId}/imports/${batchId}/start`, {})).data;
export const listImports = async (reportId) => (await api.get(`/expenses/${reportId}/imports`)).data;
export const cancelImport = async (reportId, batchId) => (await api.delete(`/expenses/${reportId}/imports/${batchId}`)).data;
export const dismissImport = async (reportId, batchId) => (await api.post(`/expenses/${reportId}/imports/${batchId}/seen`, {})).data;

// HR, Finance or an Admin change the exchange rate of a report (until it is approved).
export const setExpenseRate = async (reportId, currency, rate) => (await api.put(`/expenses/${reportId}/rates/${currency}`, { rate })).data;

// The rate on a day, for a line being typed on a report that has no rate for that currency yet.
export const previewRate = async (from, date) => (await api.get('/expenses/rate', { params: { from, date } })).data;

// The owner has looked at the lines read from receipts.
export const confirmChecked = async (reportId) => (await api.post(`/expenses/${reportId}/items/confirm`, {})).data;
